import React, { useCallback, useMemo } from 'react';
import { ExperienceFailure } from '@helpcenter/analytics-components/src/experience-notifier';
import { ScreenName } from '@helpcenter/constants';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { ElementErrorUI } from '@helpcenter/layout-primitives-util/src/element-error-ui';
import { noop } from 'lodash';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import { editHomepageExperience } from 'view/advanced-help-center/edit-homepage/experiences';
import { viewHomepageExperience } from 'view/advanced-help-center/view-homepage/experiences';
import { useLayoutActions } from 'view/layout-next/controllers';
import { Experience, ExperienceStore } from '@atlassian/help-center-common-util/experience-store';
import type { FetchError } from '@atlassian/help-center-common-util/fetch';
import { isFetchNetworkOrClientError } from '@atlassian/help-center-common-util/fetch';
import { UfoExperienceSuccess } from '@atlassian/help-center-common-util/ufo-experience';
import type { homePageElementViewRendererFragment$key } from '@atlassian/help-center-home-page-element-view-renderer/src/ui/__generated__/homePageElementViewRendererFragment.graphql';
import type { UFOExperience } from '@atlassian/ufo/ufo-experimental';
import { useUFOComponentExperience } from '@atlassian/ufo/use-component-experience';
import type { elementErrorBoundaryFragment$key } from './__generated__/elementErrorBoundaryFragment.graphql';
import messages from './messages';

interface ElementErrorBoundaryProps {
	helpLayoutElement: elementErrorBoundaryFragment$key | homePageElementViewRendererFragment$key;
	screenName: ScreenName.VIEW_HOMEPAGE | ScreenName.EDIT_HOMEPAGE;
	children: React.ReactNode;
}

const elementExperienceStore = new ExperienceStore();

const experienceMapper = (elementIdentifier: string): Experience => {
	switch (elementIdentifier) {
		case 'EDITOR':
			return Experience.RENDER_EDITOR_ELEMENT;
		case 'HERO':
			return Experience.RENDER_HERO_ELEMENT;
		case 'ANNOUNCEMENT':
			return Experience.RENDER_ANNOUNCEMENT_ELEMENT;
		case 'TOPICS_LIST':
			return Experience.RENDER_TOPICS_LIST_ELEMENT;
		case 'PORTALS_LIST':
			return Experience.RENDER_PORTALS_LIST_ELEMENT;
		case 'SUGGESTED_REQUEST_FORMS_LIST':
			return Experience.RENDER_SUGGESTED_REQUEST_FORMS_LIST_ELEMENT;
		case 'BREADCRUMB':
			return Experience.RENDER_BREADCRUMB_ELEMENT;
		case 'FORGE':
			return Experience.RENDER_FORGE_ELEMENT;
		case 'NO_CONTENT':
			return Experience.RENDER_NO_CONTENT_ELEMENT;
		default:
			return Experience.RENDER_UNKNOWN_ELEMENT;
	}
};

const ElementErrorBoundary: React.FC<ElementErrorBoundaryProps> = ({
	screenName,
	children,
	helpLayoutElement,
}) => {
	const data = useFragment(
		graphql`
			fragment elementErrorBoundaryFragment on HelpLayoutElement {
				__typename
				... on HelpLayoutAnnouncementElement {
					elementType {
						key
					}
				}
				... on HelpLayoutEditorElement {
					elementType {
						key
					}
				}
				... on HelpLayoutHeroElement {
					elementType {
						key
					}
				}
				... on HelpLayoutPortalsListElement {
					elementType {
						key
					}
				}
				... on HelpLayoutTopicsListElement {
					elementType {
						key
					}
				}
				... on HelpLayoutSuggestedRequestFormsListElement {
					elementType {
						key
					}
				}
				... on HelpLayoutBreadcrumbElement {
					elementType {
						key
					}
				}
				... on HelpLayoutNoContentElement {
					elementType {
						key
					}
				}
				... on HelpLayoutForgeElement {
					elementType {
						key
					}
				}
				... on HelpLayoutConnectElement {
					elementType {
						key
					}
				}
				... on QueryError {
					identifier
				}
			}
		`,
		helpLayoutElement,
	);
	const { formatMessage } = useIntl();

	const [state] = useLayoutActions();
	let experience =
		screenName === ScreenName.VIEW_HOMEPAGE ? viewHomepageExperience : editHomepageExperience;
	if (state.experience) {
		experience = state.experience;
	}

	let elementIdentifier: string = '';

	if (data.__typename === 'QueryError') {
		elementIdentifier =
			(data as unknown as { identifier: string | null | undefined }).identifier || 'UNKNOWN';
	} else {
		// added addition "?" check, because of a bug https://hello.jira.atlassian.cloud/browse/PORTAL-5454
		elementIdentifier =
			(data as unknown as { elementType: { key: string | null | undefined } })?.elementType?.key ||
			'UNKNOWN';
	}
	const targetExperience = React.useRef<UFOExperience>(
		elementExperienceStore.createNewUFOExperience({
			experience: experienceMapper(elementIdentifier),
		}),
	);

	useUFOComponentExperience(targetExperience.current);

	// Define a list of valid element identifiers
	const validIdentifiers = [
		'EDITOR',
		'HERO',
		'ANNOUNCEMENT',
		'TOPICS_LIST',
		'PORTALS_LIST',
		'SUGGESTED_REQUEST_FORMS_LIST',
		'FORGE',
		'CONNECT',
		'NO_CONTENT',
		'BREADCRUMB',
	];

	// Default to UNKNOWN if the element type is not recognized
	elementIdentifier = validIdentifiers.includes(elementIdentifier) ? elementIdentifier : 'UNKNOWN';

	// TODO: Remove this check once we have a better way to handle suggested request form errors
	const viewSuggestedRequestFormError = useMemo(() => {
		return (
			screenName === ScreenName.VIEW_HOMEPAGE &&
			elementIdentifier === 'SUGGESTED_REQUEST_FORMS_LIST'
		);
	}, [elementIdentifier, screenName]);

	const rethrowError = useCallback((error: Error) => {
		throw error;
	}, []);

	const traceId = state.traceId;

	const markElementExperienceFailed = useCallback(
		(elementExperience: UFOExperience, error: Error | FetchError) => {
			if (!isFetchNetworkOrClientError(error as FetchError)) {
				elementExperience
					.failure({
						metadata: {
							traceId,
							screenName: state.screenName,
							error: error?.message,
							statusCode: (error as FetchError)?.status,
							failureEvent: state.screenName,
						},
					})
					.then(noop)
					.catch(noop);
			}
		},
		[state.screenName, traceId],
	);

	/**
	 * If any error happens inside layout viewer/builder
	 * 1. It should fail the entire experience - UFO
	 * 2. Return null by default
	 * 3. Send error payload to sentry and analytics - through ErrorBoundary
	 * 4. Pass reason for the failure -UFO
	 */
	const markExperienceFailed = useCallback(
		(error: Error | FetchError) => {
			if (!isFetchNetworkOrClientError(error as FetchError) && !viewSuggestedRequestFormError) {
				experience
					.failure({
						metadata: {
							traceId,
							id: elementIdentifier,
							error: error?.message,
							statusCode: (error as FetchError)?.status,
							failureEvent: state.screenName,
						},
					})
					.then(noop)
					.catch(noop);
			}

			if (!state.isPageInEditMode) {
				markElementExperienceFailed(targetExperience.current, error);
			}
			if (state.isPageInEditMode) {
				rethrowError(error as Error);
			}
		},
		[
			viewSuggestedRequestFormError,
			experience,
			traceId,
			elementIdentifier,
			state.screenName,
			state.isPageInEditMode,
			markElementExperienceFailed,
			rethrowError,
		],
	);
	const renderError = useCallback(() => {
		return (
			<ElementErrorUI
				errorMessage={formatMessage(messages.errorMessage)}
				elementType={elementIdentifier === 'UNKNOWN' ? data.__typename : elementIdentifier}
			/>
		);
	}, [data.__typename, elementIdentifier, formatMessage]);

	const renderErrorWithFailure = (error: Error | FetchError) => {
		if (!isFetchNetworkOrClientError(error as FetchError) && !viewSuggestedRequestFormError) {
			return (
				<ExperienceFailure
					location="element-error-boundary"
					errorMessage={error.message}
					traceId={traceId}
					status={(error as FetchError).status}
				>
					{renderError()}
				</ExperienceFailure>
			);
		}
		return renderError();
	};
	return (
		<ErrorBoundary
			packageName={screenName}
			id={elementIdentifier}
			renderError={
				!state.isPageInEditMode || screenName === ScreenName.VIEW_HOMEPAGE
					? renderErrorWithFailure
					: undefined
			}
			onError={markExperienceFailed}
		>
			{React.Children.only(children)}
			{!state.isPageInEditMode ? (
				<UfoExperienceSuccess
					experience={targetExperience.current}
					config={{ metadata: { screenName: state.screenName } }}
				/>
			) : null}
		</ErrorBoundary>
	);
};

export default ElementErrorBoundary;
